// @flow
import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { Grid, Row, Col, Button, Image } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import ShapeSetSidebar from './ShapeSetSidebar';
import * as shapeSetCreators from '../../actions/shapeSets/shapeSetCreators';
import * as organisationCreators from '../../actions/organisations/organisationCreators';
import { getCurrentLocationShapeSets } from '../../actions/locations/shapeSetCreators';
import ShapeSetTabSidebar from './ShapeSetTabSidebar';
import MapIcon from '../../images/map-icon.png';
import HelpWrapper from '../../components/HelpWrapper';
import { getSetting } from '../../actions/settings/settingsCreators';
import type { Organisation } from '../../types';

type Props = {
  params: {
    context: string,
    organisationId: string,
    shapeSetId: string
  },
  children: any,
  router: any,
  currentOrganisation: Organisation,
  currentLocation: Object,
  shapeSets: Object,
  settings: Object,
  currentShapeSet: Object,
  updateCurrentOrganisation: (oid: string, s: boolean) => void,
  getSetting: (s: string) => void,
  getGlobalShapeSets: Function,
  getCurrentOrganisationShapeSets: Function,
  getCurrentLocationShapeSets: Function,
  updateShapeSet: Function,
  addShapeSet: Function,
  clearCurrentShapeSet: Function,
  getShapeSetSettings: Function,
  updateCurrentShapeSet: Function
};

type State = {
  shapeSetsLoaded: boolean
};

class ShapeSets extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      shapeSetsLoaded: false
    };
  }

  componentDidMount() {
    const context = this.props.params.context;
    const { getSetting } = this.props;
    this.getShapeSets(this.props, context).then(
      success => this.setState({ shapeSetsLoaded: true }),
      failure => console.log("Couldn't get shapesets. Context: ", context)
    );
    // Update organisation
    const { organisationId } = this.props.params;
    this.props.updateCurrentOrganisation(organisationId, true);

    getSetting('catchmentSetHelpPdf');
    getSetting('catchmentSetHelpVideo');
  }

  handleSubmit = values => {
    if (values.id) {
      this.props.updateShapeSet(values);
    } else {
      this.props.addShapeSet(values);
    }
  };

  componentWillUnmount() {
    localStorage.removeItem('backUrl');
  }

  componentWillReceiveProps(nextProps) {
    // if context changes
    const oldContext = this.props.params.context;
    const newContext = nextProps.params.context;
    if (oldContext !== newContext) {
      this.getShapeSets(nextProps, newContext);
    }

    // if current org changes
    const oldOrganisationId = this.props.params.organisationId;
    const newOrganisationId = nextProps.params.organisationId;
    const oldCurrentOrganisation = this.props.currentOrganisation;
    const newCurrentOrganisation = nextProps.currentOrganisation;

    if (
      oldOrganisationId !== newOrganisationId ||
      oldCurrentOrganisation.id !== newCurrentOrganisation.id
    ) {
      this.getShapeSets(nextProps, newContext);
      const shapeSetUrl = `/${newContext}/${newOrganisationId}/catchment-sets`;
      this.props.router.push(shapeSetUrl);
    }

    // if shapesetId in url changes
    const currentShapeSetId = this.props.params.shapeSetId;
    const newCurrentShapeSetId = nextProps.params.shapeSetId;

    if (newCurrentShapeSetId !== currentShapeSetId) {
      this.handleShapeSetIdUpdate(newCurrentShapeSetId);
    }
  }

  getShapeSets = (props, context) => {
    const isAllocatedTo = true;
    switch (context) {
      case 'global':
        return props.getGlobalShapeSets();
      default:
      case 'customer':
        const organisationId = props.params.organisationId;
        return props.getCurrentOrganisationShapeSets(
          organisationId,
          isAllocatedTo
        );
      case 'sub-account':
        const locationId = props.params.organisationId;
        return props.getCurrentLocationShapeSets(locationId, isAllocatedTo);
    }
  };

  shapeSetsByContext = () => {
    const {
      params,
      currentOrganisation,
      currentLocation,
      shapeSets
    } = this.props;
    switch (params.context) {
      case 'global':
        return shapeSets;
      case 'customer':
        return currentOrganisation.shapeSets.allocated;
      case 'sub-account':
        return currentLocation.shapeSets.allocated;
      default:
        return [];
    }
  };

  handleShapeSetIdUpdate = newShapeSetId => {
    const context = this.props.params.context;

    // url: /catchment-sets
    if (!newShapeSetId) {
      this.props.clearCurrentShapeSet();
      return;
    }

    // url: /catchment-sets/new
    if (newShapeSetId === 'new') {
      this.props.clearCurrentShapeSet();
      this.props.getShapeSetSettings(context);
      return;
    }

    // url: /catchment-sets/10
    this.props.updateCurrentShapeSet(parseInt(newShapeSetId, 10), context);
  };

  getContext = () => {
    // needs to be updated for locations
    let context = this.props.params.context;
    if (context !== 'global' && context !== 'customer') {
      context = 'customer';
    }
    return context;
  };

  render() {
    const currentComponent = this.props.children
      ? this.props.children.type.displayName
      : '';
    const { settings } = this.props;
    const context = this.getContext();
    const shapeSets = this.shapeSetsByContext();
    const backUrl = localStorage.getItem('backUrl');
    const shapeSetBaseUrl = `/${context}/${
      this.props.params.organisationId
    }/catchment-sets`;

    return (
      <Grid fluid>
        <Helmet>
          <title>Manage Catchment Sets | Campaign Targeter</title>
        </Helmet>
        <Row>
          <Col sm={3} className="sidebar-wrapper">
            {this.state.shapeSetsLoaded && (
              <ShapeSetSidebar {...this.props} items={shapeSets} />
            )}
          </Col>
          <Col sm={9} className="shape-set-maincontent">
            <div>
              <Row>
                <HelpWrapper
                  pdfUrl={
                    settings && settings.catchmentSetHelpPdf
                      ? settings.catchmentSetHelpPdf.value
                      : undefined
                  }
                  videoUrl={
                    settings && settings.catchmentSetHelpVideo
                      ? settings.catchmentSetHelpVideo.value
                      : undefined
                  }
                  extraClass={this.props.children ? 'child-page-present' : ''}
                />
              </Row>
              {!this.props.children && (
                <div>
                  <Link to={`${shapeSetBaseUrl}/new`} className="well-300">
                    <Image src={MapIcon} />
                    <Button bsStyle="primary">Create new Catchment Set</Button>
                  </Link>
                </div>
              )}
            </div>
            {backUrl && (
              <Link to={backUrl} className="back-link">
                &#11104; Back to customer
              </Link>
            )}
            {this.props.children && (
              <Row className="shape-set-details">
                <Col sm={4} lg={3} className="shape-set-sidebar">
                  <ShapeSetTabSidebar
                    activeTab={currentComponent}
                    context={context}
                    shapeSetId={this.props.params.shapeSetId}
                    organisationId={this.props.params.organisationId}
                    shareWithAllIndustries={
                      this.props.currentShapeSet.shareWithAllIndustries
                    }
                  />
                </Col>
                <Col sm={8} lg={9} className="shape-set-maincontent">
                  {React.cloneElement(this.props.children, { ...this.props })}
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Grid>
    );
  }
}

function mapStateToProps(state) {
  return {
    currentOrganisation: state.currentOrganisation,
    currentLocation: state.currentLocation,
    shapeSets: state.shapeSets,
    currentShapeSet: state.currentShapeSet,
    authentication: state.authentication,
    settings: state.settings
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      ...shapeSetCreators,
      ...organisationCreators,
      getCurrentLocationShapeSets,
      getSetting
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ShapeSets);



// WEBPACK FOOTER //
// ./src/containers/shapeset/ShapeSets.js